import React, {
  useEffect,
  ReactNode,
  useMemo,
  useContext,
  useState,
} from "react";
import { Badge } from "@src/common/components";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Loading } from "@common/components/Loading";
import { useCourieStore } from "@src/common/lib/store";
import { formatDateWithDay, isToday } from "@src/common/lib/DateUtils";
import {
  ArrowTopRightOnSquareIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Car } from "lucide-react";
import {
  DispatchPreview,
  DispatchPreviewSaveInput,
  Driver,
  DriverPaySummary,
  DriverWorkType,
  GetDispatchPreviewsQueryVariables,
  ShipmentStatus,
  TaskStatus,
  RoutingJobStatus,
  Stop,
} from "@api/graphql/generated/generated-types";
import type { DispatchAdditionalFilters } from "@api/graphql/generated/generated-types";
import AssignedDriverView from "../AssignedDriverView";
import { ExpandableDriverCard } from "@src/shipments/components/ExpandableDriverCard";
import {
  driversToDispatchCandidateViews,
  makeDispatchCandidateViews,
} from "@src/shipments/utils/makeDispatchCandidateViews";
import {
  DispatchCandidateSortingType,
  ShipmentRow,
} from "@src/shipments/types";
import DispatchCandidateSortDropdown from "@src/shipments/components/DispatchCandidateSortDropdown";
import { MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN } from "@src/shipments/constants/styleConstants";
import classNames from "classnames";
import { DRIVER_API_LIMIT } from "@src/common/constants/apiConstants";
import { AuthContext } from "@src/auth/components/AuthProvider";
import {
  CandidatesProvider,
  useCandidates,
} from "./contexts/CandidatesProvider";
import {
  CandidatesGraphQLProvider,
  useCandidatesGraphQL,
} from "./contexts/CandidatesGraphQLProvider";
import CourieButton from "../../Button/Button";
import { useRouter } from "next/router";
import { getStatusMessage } from "@src/routingJob/utils/getRoutingJobStatusMessage";
import { sortAndGroupDrivers } from "./utils/driverSorting";
import { pickOtherOnlineDrivers } from "./utils/pickOtherOnlineDrivers";
import { useOrderIdentifierColumnType } from "@src/shipments/hooks/useOrderIdentifierColumnType";
import { resolveOrderIdentifier } from "@src/shipments/utils/orderIdentifierColumn";
import {
  getThisWeekRangeForDriverPay,
  getTodayRangeForDailyDriverEarnings,
} from "./utils/driverPayRange";
import { DriverDispatchCandidate } from "@src/shipments/utils/makeDispatchCandidateViews";
import { useDispatchByDriverId } from "./hooks/useDispatchByDriverId";
import { useDispatchDelayCountdown } from "./hooks/useDispatchDelayCountdown";
import AddOrderList from "../AddOrderList";
import { ClockIcon } from "@heroicons/react/24/outline";
import { DispatchCandidateFilterPopover } from "./components/DispatchCandidateFilterPopover";
import { DispatchCandidateSearchPopover } from "./components/DispatchCandidateSearchPopover";

export type DispatchCandidateView = {
  id: number | string;
  title: string;
  fullName: string;
  subText: ReactNode;
  image: string;
  badgeText?: string;
  /** "gray" for exclusion tags (Too far, Wrong vehicle, etc.); omit for green success badges */
  badgeColor?: "success" | "gray";
  distanceInMeters?: number;
  distanceAdditionalInMeters?: number;
  vehicleType?: string;
  remainingWorkloadInSeconds?: number;
  phone?: string;
  isActive: boolean;
  driver: Driver;
  dispatchPreview: DispatchPreview | undefined;
  dailyEarnings?: DriverPaySummary | null;
  workType: DriverWorkType;
  __typename: string;
};

enum DispatchPreviewSection {
  ONLINE = "ONLINE",
  OTHER_ONLINE = "OTHER_ONLINE",
  OFFLINE = "OFFLINE",
  SPECIALIZED = "SPECIALIZED",
}

export type DispatchCandidatesWidgetProps = {
  selectedRow: ShipmentRow | undefined;
  onClickUnassign?: () => void;
  onClickChangeRouting?: () => void;
  onClickTransferDriver?: () => void;
  isAssignDriverModalOpen: boolean;
  setIsAssignDriverModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDriver: Driver | undefined;
  setSelectedDriver: React.Dispatch<React.SetStateAction<Driver | undefined>>;
  setDispatchPreviewSortingType: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  setDispatchPreviews: React.Dispatch<
    React.SetStateAction<
      DispatchPreviewSaveInput | DispatchPreviewSaveInput[] | undefined
    >
  >;
  setDriverRank: React.Dispatch<React.SetStateAction<number | undefined>>;
  setDispatchPreviewSection: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  targetDate: string;
  showOrderList: boolean;
  setShowOrderList: (showOrderList: boolean) => void;
  onSelectPreview: (preview: DispatchPreview) => void;
  setHoveredStopsFromWidget: (
    hoveredStopsFromWidget: Stop[] | undefined
  ) => void;
};

const taskStatusFilterForDispatchPreviews = [
  TaskStatus.Created,
  TaskStatus.Started,
];

function DispatchCandidatesWidget({
  selectedRow,
  onClickUnassign,
  onClickChangeRouting,
  onClickTransferDriver,
  setIsAssignDriverModalOpen,
  setSelectedDriver,
  setDispatchPreviewSortingType,
  setDispatchPreviews,
  setDriverRank,
  setDispatchPreviewSection,
  targetDate,
  showOrderList,
  setShowOrderList,
  selectedDriver,
  onSelectPreview,
  setHoveredStopsFromWidget,
}: DispatchCandidatesWidgetProps) {
  // contexts
  const { courierId } = useContext(AuthContext);
  const orderIdentifierColumnType = useOrderIdentifierColumnType();
  const { setSelectedWidgetItem } = useCourieStore();
  const router = useRouter();
  // UX-only client-side filter on the already-loaded candidates by driver name
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const {
    candidates,
    setCandidates,
    excludedCandidates,
    setExcludedCandidates,
    unavailableCandidates,
    setUnavailableCandidates,
    specializedCandidates,
    setSpecializedCandidates,
    dispatchCandidatesSortingType,
    setDispatchCandidatesSortingType,
    isDistanceFilterEnabled,
    isVehicleTypeFilterEnabled,
    isWeightFilterEnabled,
    isShowDriverLabelsEnabled,
    isShowDriverPayEnabled,
    isShowDailyEarningsEnabled,
    selectedDriverLabelIds,
  } = useCandidates();
  const {
    FetchDriverTasksByDateAndStatus,
    FetchDriverTasksByDateAndStatusData,
    FetchDriverTasksByDateAndStatusLoading,
    getDispatchPreviews,
    dispatchPreviewApiData,
    dispatchPreviewLoading,
    dispatchPreviewApiDataSpecialized,
  } = useCandidatesGraphQL();
  // Online regular drivers from the plain drivers list; "Other Online Drivers" is built from these
  const [onlineRegularDrivers, setOnlineRegularDrivers] = useState<
    DriverDispatchCandidate[]
  >([]);
  const { onClickAddOrder } = useDispatchByDriverId({});
  // Stable effect dependency: the label array may be a new instance on every render
  const selectedDriverLabelIdsKey = selectedDriverLabelIds.join(",");

  const { remainingSeconds, formattedRemainingTime } =
    useDispatchDelayCountdown(
      selectedRow?.shipment ? [selectedRow?.shipment] : undefined
    );

  useEffect(() => {
    setDispatchPreviewSortingType(dispatchCandidatesSortingType);
  }, [dispatchCandidatesSortingType]);

  const selectedShipmentsMemo = useMemo(
    () => selectedRow?.shipment,
    [
      selectedRow?.shipment?.order.displayId,
      selectedRow?.shipment?.trackingNumber,
      selectedRow?.shipment?.driver,
    ]
  );
  const selectedOrderNumber = resolveOrderIdentifier(
    {
      trackingNumber: selectedShipmentsMemo?.trackingNumber,
      displayId: selectedShipmentsMemo?.order?.displayId,
    },
    orderIdentifierColumnType
  );

  const selectedShipmentDate = selectedShipmentsMemo?.shipmentDate;
  const canUnassignDriver = selectedRow?.canUnassignDriver;
  const canChangeRouting = selectedRow?.canChangeRouting;
  const canTransferDriver =
    selectedRow?.shipment?.status !== ShipmentStatus.Completed;
  let showWarningBadge = false;

  if (
    selectedShipmentsMemo &&
    !isToday(selectedShipmentsMemo.firstEffectiveStopDate)
  ) {
    showWarningBadge = true;
  }

  useEffect(() => {
    if (
      dispatchCandidatesSortingType ===
      DispatchCandidateSortingType.ALPHABETICAL
    ) {
      setDispatchCandidatesSortingType(
        DispatchCandidateSortingType.LEAST_DEVIATION
      );
      return;
    }

    fetchDispatchPreviews();
  }, [
    selectedShipmentsMemo,
    isDistanceFilterEnabled,
    isVehicleTypeFilterEnabled,
    isWeightFilterEnabled,
    isShowDriverPayEnabled,
    isShowDailyEarningsEnabled,
  ]);

  useEffect(() => {
    if (
      dispatchCandidatesSortingType ===
        DispatchCandidateSortingType.ALPHABETICAL &&
      courierId &&
      selectedShipmentDate &&
      selectedShipmentsMemo
    ) {
      const weekPay = getThisWeekRangeForDriverPay(isShowDriverPayEnabled);
      const dailyPay = getTodayRangeForDailyDriverEarnings(isShowDailyEarningsEnabled);
      FetchDriverTasksByDateAndStatus({
        variables: {
          courierId: courierId,
          date: selectedShipmentsMemo.firstEffectiveStopDate,
          limit: DRIVER_API_LIMIT,
          statusFilter: [TaskStatus.Created, TaskStatus.Started],
          payMinDate: weekPay.minDate,
          payMaxDate: weekPay.maxDate,
          payShipmentStatuses: weekPay.shipmentStatuses,
          shouldFetchDriverPay: weekPay.shouldFetch,
          dailyPayMinDate: dailyPay.minDate,
          dailyPayMaxDate: dailyPay.maxDate,
          dailyPayShipmentStatuses: dailyPay.shipmentStatuses,
          shouldFetchDailyEarnings: dailyPay.shouldFetch,
        },
      });
    } else {
      fetchDispatchPreviews();
    }
  }, [
    dispatchCandidatesSortingType,
    selectedShipmentsMemo,
    isDistanceFilterEnabled,
    isVehicleTypeFilterEnabled,
    isWeightFilterEnabled,
    isShowDriverPayEnabled,
    isShowDailyEarningsEnabled,
    selectedDriverLabelIdsKey,
  ]);

  useEffect(() => {
    if (!selectedShipmentsMemo || !dispatchPreviewApiData) {
      return;
    }
    if (
      dispatchCandidatesSortingType !==
      DispatchCandidateSortingType.ALPHABETICAL
    ) {
      setCandidates(
        makeDispatchCandidateViews(
          dispatchPreviewApiData,
          dispatchCandidatesSortingType
        )
      );
    }
  }, [dispatchPreviewApiData]);

  // "Other Online Drivers" = online regular drivers not in the recommended list.
  // No route previews are computed for them, which saves server CPU.
  useEffect(() => {
    if (
      !selectedShipmentsMemo ||
      !dispatchPreviewApiData ||
      dispatchCandidatesSortingType ===
        DispatchCandidateSortingType.ALPHABETICAL
    ) {
      return;
    }
    const recommendedDriverIds = new Set<string>(
      (dispatchPreviewApiData.dispatchPreviews ?? []).map(
        (dispatchPreview) => dispatchPreview.driver.id
      )
    );
    setExcludedCandidates(
      driversToDispatchCandidateViews(
        pickOtherOnlineDrivers(onlineRegularDrivers, recommendedDriverIds)
      )
    );
  }, [
    dispatchPreviewApiData,
    onlineRegularDrivers,
    dispatchCandidatesSortingType,
    selectedShipmentsMemo,
  ]);

  useEffect(() => {
    if (
      dispatchCandidatesSortingType !==
      DispatchCandidateSortingType.ALPHABETICAL
    ) {
      if (!selectedShipmentsMemo || !courierId) {
        return;
      }
      const weekPay = getThisWeekRangeForDriverPay(isShowDriverPayEnabled);
      const dailyPay = getTodayRangeForDailyDriverEarnings(isShowDailyEarningsEnabled);
      FetchDriverTasksByDateAndStatus({
        variables: {
          courierId: courierId,
          date: selectedShipmentsMemo.firstEffectiveStopDate,
          limit: DRIVER_API_LIMIT,
          statusFilter: [TaskStatus.Created, TaskStatus.Started],
          payMinDate: weekPay.minDate,
          payMaxDate: weekPay.maxDate,
          payShipmentStatuses: weekPay.shipmentStatuses,
          shouldFetchDriverPay: weekPay.shouldFetch,
          dailyPayMinDate: dailyPay.minDate,
          dailyPayMaxDate: dailyPay.maxDate,
          dailyPayShipmentStatuses: dailyPay.shipmentStatuses,
          shouldFetchDailyEarnings: dailyPay.shouldFetch,
        },
        fetchPolicy: "network-only",
      }).then((res) => {
        const nodes = res.data?.drivers?.edges?.map((edge) => edge?.node);
        if (!nodes) {
          return;
        }

        const driverResults: DriverDispatchCandidate[] = nodes.filter(Boolean).map((node) => ({
          driver: node as unknown as Driver,
          dailyEarnings: (node as any).dailyEarnings ?? null,
        }));

        const { onlineRegular, offlineRegular, specialized } =
          sortAndGroupDrivers(driverResults, dispatchCandidatesSortingType);

        setOnlineRegularDrivers(onlineRegular);
        setUnavailableCandidates(driversToDispatchCandidateViews(offlineRegular));
        setSpecializedCandidates(driversToDispatchCandidateViews(specialized));
      });
    }
  }, [
    dispatchCandidatesSortingType,
    selectedShipmentsMemo,
    isShowDriverPayEnabled,
    isShowDailyEarningsEnabled,
  ]);

  useEffect(() => {
    if (
      dispatchCandidatesSortingType !==
        DispatchCandidateSortingType.ALPHABETICAL &&
      dispatchPreviewApiDataSpecialized
    ) {
      setSpecializedCandidates(
        makeDispatchCandidateViews(
          dispatchPreviewApiDataSpecialized,
          dispatchCandidatesSortingType
        )
      );
    }
  }, [dispatchCandidatesSortingType, dispatchPreviewApiDataSpecialized]);

  useEffect(() => {
    if (
      !FetchDriverTasksByDateAndStatusData ||
      dispatchCandidatesSortingType !==
        DispatchCandidateSortingType.ALPHABETICAL
    ) {
      return;
    }
    const nodes = FetchDriverTasksByDateAndStatusData?.drivers?.edges?.map(
      (edge) => edge?.node
    );
    if (nodes) {
      const driverResults: DriverDispatchCandidate[] = nodes.filter(Boolean).map((node) => ({
        driver: node as unknown as Driver,
        dailyEarnings: (node as any).dailyEarnings ?? null,
      }));

      const { onlineRegular, offlineRegular, specialized } =
        sortAndGroupDrivers(driverResults, dispatchCandidatesSortingType);

      setCandidates(driversToDispatchCandidateViews(onlineRegular));
      setExcludedCandidates([]);
      setUnavailableCandidates(driversToDispatchCandidateViews(offlineRegular));
      setSpecializedCandidates(driversToDispatchCandidateViews(specialized));
    }
  }, [dispatchCandidatesSortingType, FetchDriverTasksByDateAndStatusData]);

  const filterByDriverLabels = (list: DispatchCandidateView[]) => {
    if (selectedDriverLabelIds.length === 0) return list;
    return list.filter((candidate) =>
      candidate.driver.assignedLabels?.some((label) =>
        selectedDriverLabelIds.includes(label.id)
      )
    );
  };

  // Case-insensitive substring match on driver name; preserves existing order/sort.
  const filterByDriverSearch = (list: DispatchCandidateView[]) => {
    const term = driverSearchTerm.trim().toLowerCase();
    if (term.length === 0) return list;
    return list.filter((candidate) =>
      `${candidate.driver.firstName} ${candidate.driver.lastName}`
        .toLowerCase()
        .includes(term)
    );
  };

  const filterCandidates = (list: DispatchCandidateView[]) =>
    filterByDriverSearch(filterByDriverLabels(list));

  const availableCandidates = filterCandidates(
    candidates?.filter(
      (candidate) =>
        candidate.isActive && candidate.workType === DriverWorkType.Regular
    ) ?? []
  );
  const filteredExcludedCandidates = filterCandidates(excludedCandidates ?? []);
  const filteredUnavailableCandidates = filterCandidates(
    unavailableCandidates ?? []
  );
  const filteredSpecializedCandidates = filterCandidates(
    specializedCandidates ?? []
  );
  // "Other Online Drivers" needs both the recommended list and the plain drivers list
  const isOtherOnlineLoading =
    dispatchPreviewLoading || FetchDriverTasksByDateAndStatusLoading;

  const isSearching = driverSearchTerm.trim().length > 0;
  const hasNoSearchResults =
    isSearching &&
    availableCandidates.length === 0 &&
    filteredExcludedCandidates.length === 0 &&
    filteredUnavailableCandidates.length === 0 &&
    filteredSpecializedCandidates.length === 0;

  const buildAdditionalFilters = (
    includeDrivers: boolean
  ): DispatchAdditionalFilters => ({
    includeDrivers,
    maxDistanceInMetersIfLocationExists: isDistanceFilterEnabled
      ? 160934
      : null,
    vehicleTypeIdIfExists: isVehicleTypeFilterEnabled
      ? selectedShipmentsMemo?.vehicleType?.id ?? null
      : null,
    minimumVehicleCapacityInPoundsIfExists: isWeightFilterEnabled
      ? selectedShipmentsMemo &&
        selectedShipmentsMemo.packageWeightInPounds !== null &&
        selectedShipmentsMemo.packageWeightInPounds !== undefined
        ? Math.ceil(selectedShipmentsMemo.packageWeightInPounds)
        : null
      : null,
  });

  const fetchDispatchPreviews = () => {
    if (
      !selectedShipmentsMemo ||
      selectedShipmentsMemo.status !== ShipmentStatus.Created
    ) {
      return;
    }
    const baseVars = {
      courierId: courierId as string,
      isActive: true,
      workType: DriverWorkType.Regular,
      shipmentId: selectedShipmentsMemo.id,
      taskStatusFilter: taskStatusFilterForDispatchPreviews,
      shipmentDate: selectedShipmentsMemo.firstEffectiveStopDate,
      // Let the backend skip drivers the label filter would hide anyway
      driverLabelIds:
        selectedDriverLabelIds.length > 0 ? selectedDriverLabelIds : undefined,
    };
    const filtersIncluded = buildAdditionalFilters(true);
    const payRange = getThisWeekRangeForDriverPay(isShowDriverPayEnabled);
    const dailyRange = getTodayRangeForDailyDriverEarnings(isShowDailyEarningsEnabled);

    getDispatchPreviews({
      variables: {
        ...baseVars,
        payMinDate: payRange.minDate,
        payMaxDate: payRange.maxDate,
        payShipmentStatuses: payRange.shipmentStatuses,
        shouldFetchDriverPay: payRange.shouldFetch,
        dailyPayMinDate: dailyRange.minDate,
        dailyPayMaxDate: dailyRange.maxDate,
        dailyPayShipmentStatuses: dailyRange.shipmentStatuses,
        shouldFetchDailyEarnings: dailyRange.shouldFetch,
        additionalFilters: filtersIncluded,
      } as GetDispatchPreviewsQueryVariables,
      fetchPolicy: "cache-and-network",
    });
  };

  const keyBoardSelectedDriverId = 0;

  function findDriverRankingAndSet(selectedDriver: Driver) {
    if (!selectedShipmentsMemo) {
      return;
    }

    const onlineIndex = candidates.findIndex(
      (candidate) => candidate.driver.id === selectedDriver.id
    );
    const excludedIndex = excludedCandidates.findIndex(
      (candidate) => candidate.driver.id === selectedDriver.id
    );
    const offlineIndex = unavailableCandidates.findIndex(
      (candidate) => candidate.driver.id === selectedDriver.id
    );
    const specializedIndex = specializedCandidates.findIndex(
      (candidate) => candidate.driver.id === selectedDriver.id
    );

    let dispatchPreviewSection = "";
    let driverRank = 0;

    if (onlineIndex !== -1) {
      dispatchPreviewSection = DispatchPreviewSection.ONLINE;
      driverRank = onlineIndex + 1;
    } else if (excludedIndex !== -1) {
      dispatchPreviewSection = DispatchPreviewSection.OTHER_ONLINE;
      driverRank = candidates.length + excludedIndex + 1;
    } else if (offlineIndex !== -1) {
      dispatchPreviewSection = DispatchPreviewSection.OFFLINE;
      driverRank =
        candidates.length +
        excludedCandidates.length +
        offlineIndex +
        1;
    } else if (specializedIndex !== -1) {
      dispatchPreviewSection = DispatchPreviewSection.SPECIALIZED;
      driverRank =
        candidates.length +
        excludedCandidates.length +
        unavailableCandidates.length +
        specializedIndex +
        1;
    }

    setDriverRank(driverRank);
    setDispatchPreviewSection(dispatchPreviewSection || null);

    const toPreview = (
      candidate: DispatchCandidateView,
      section: string
    ): DispatchPreviewSaveInput => ({
      distanceAdditionalInMeters: candidate.distanceAdditionalInMeters,
      distanceInMeters: candidate.dispatchPreview?.distanceInMeters,
      driverId: candidate.driver.id,
      durationAdditionalInSeconds:
        candidate.dispatchPreview?.durationAdditionalInSeconds,
      durationInSeconds: candidate.dispatchPreview?.durationInSeconds,
      numCurrentTasks: candidate.driver.tasks?.length || 0,
      remainingWorkloadInSeconds: candidate.remainingWorkloadInSeconds,
      section,
      shipmentId: selectedShipmentsMemo.id,
      tags: candidate.dispatchPreview?.tags || [],
    });

    const dispatchPreviews: DispatchPreviewSaveInput[] = [
      ...candidates.map((c) => toPreview(c, DispatchPreviewSection.ONLINE)),
      ...excludedCandidates.map((c) =>
        toPreview(c, DispatchPreviewSection.OTHER_ONLINE)
      ),
      ...unavailableCandidates.map((c) =>
        toPreview(c, DispatchPreviewSection.OFFLINE)
      ),
      ...specializedCandidates.map((c) =>
        toPreview(c, DispatchPreviewSection.SPECIALIZED)
      ),
    ];

    setDispatchPreviews(dispatchPreviews);
  }

  const getLoadingState = (loading: boolean) => {
    if (
      dispatchCandidatesSortingType ===
      DispatchCandidateSortingType.ALPHABETICAL
    ) {
      return FetchDriverTasksByDateAndStatusLoading;
    } else {
      return loading;
    }
  };

  if (
    selectedRow?.shipment?.status === ShipmentStatus.Created &&
    selectedRow?.shipment?.routingJobId &&
    selectedRow?.shipment?.routingJob
  ) {
    const isReady =
      selectedRow?.shipment?.routingJob.status === RoutingJobStatus.Ready;
    const isFailed =
      selectedRow?.shipment?.routingJob.status ===
      RoutingJobStatus.OptimizationFailed;
    const statusMessage = getStatusMessage(
      selectedRow?.shipment?.routingJob.status,
      selectedRow?.shipment?.routingJob.canceledBy || undefined,
      selectedRow?.shipment?.routingJob.cancelDescription || undefined
    );

    return (
      <div className="h-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-t-3xl ">
        <span className="text-3xl justify-center flex pt-20">
          {isReady ? (
            <DocumentMagnifyingGlassIcon className="w-8 h-8 text-gray-600 dark:text-gray-400" />
          ) : (
            statusMessage?.icon
          )}
        </span>
        <div className="flex items-center justify-center pb-0 text-gray-900 dark:text-gray-100">
          Order {selectedOrderNumber}
        </div>
        <div className="flex items-center justify-center p-2 pt-0 text-slate-600 dark:text-gray-400 text-sm">
          {isReady ? "Review Routes" : statusMessage?.message}
        </div>
        {!isFailed && (
          <div className="flex items-center justify-center p-2 pt-0 text-slate-600 dark:text-gray-400 text-sm">
            <CourieButton
              size={"xs"}
              color={"gray"}
              onClick={() => {
                selectedShipmentsMemo &&
                  router.push(
                    `/routingjob/${selectedShipmentsMemo.routingJobId}`
                  );
              }}
            >
              <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1 inline-block" />
              {isReady ? <>Review Routes</> : <>View Status</>}
            </CourieButton>
          </div>
        )}
      </div>
    );
  }

  if (
    remainingSeconds > 0 &&
    selectedShipmentsMemo?.status === ShipmentStatus.Created
  ) {
    return (
      <div className="h-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-t-3xl">
        <span className="text-3xl justify-center flex pt-20">
          <ClockIcon className="w-8 h-8 text-slate-500 dark:text-gray-400" />
        </span>
        <div className="flex items-center justify-center p-2 pt-0 text-slate-600 dark:text-gray-400 text-sm text-center px-4">
          This order is blocked from being dispatched for{" "}
          {formattedRemainingTime}
        </div>
      </div>
    );
  }

  if (
    selectedShipmentsMemo?.status === ShipmentStatus.Completed &&
    !selectedShipmentsMemo.driver
  ) {
    return (
      <div className="h-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-t-3xl">
        <span className="text-3xl justify-center flex pt-20">🐼</span>
        <div className="flex items-center justify-center pb-0 text-gray-900 dark:text-gray-100">
          Order {selectedOrderNumber}
        </div>
        <div className="flex items-center justify-center p-2 pt-0 text-slate-600 dark:text-gray-400 text-sm">
          has been completed without a driver
        </div>
      </div>
    );
  }

  if (
    showOrderList &&
    targetDate &&
    (selectedDriver || selectedShipmentsMemo?.driver)
  ) {
    const driver = selectedDriver || selectedShipmentsMemo?.driver;
    if (!driver) return null;
    return (
      <AddOrderList
        onBack={() => setShowOrderList(false)}
        driver={driver}
        targetDate={targetDate}
        onSelectPreview={onSelectPreview}
        setHoveredStopsFromWidget={setHoveredStopsFromWidget}
        selectedRow={selectedRow}
      />
    );
  }

  if (selectedShipmentsMemo && selectedShipmentsMemo.driver) {
    return (
      <div className="border dark:border-gray-700 rounded-3xl px-4 pt-4 pb-4 bg-white dark:bg-gray-800">
        <AssignedDriverView
          selectedRow={selectedRow}
          driver={selectedShipmentsMemo.driver}
          onClickUnassign={onClickUnassign}
          onClickChangeRouting={onClickChangeRouting}
          onClickTransferDriver={onClickTransferDriver}
          canUnassignDriver={canUnassignDriver || false}
          canChangeRouting={canChangeRouting || false}
          canTransferDriver={canTransferDriver || false}
          onClickAddOrder={onClickAddOrder}
          setShowOrderList={setShowOrderList}
        />
      </div>
    );
  }
  return (
    <>
      <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-t-3xl h-full overflow-auto">
        <div
          className="mx-auto w-full h-full"
          style={{
            maxWidth: MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN,
          }}
        >
          <div className="flex items-center justify-between gap-2 py-2 px-0">
            <DispatchCandidateSortDropdown
              sortingType={dispatchCandidatesSortingType}
              setSortingType={setDispatchCandidatesSortingType}
            />
            <div className="flex items-center gap-2">
              <DispatchCandidateSearchPopover
                searchTerm={driverSearchTerm}
                setSearchTerm={setDriverSearchTerm}
              />
              <DispatchCandidateFilterPopover />
            </div>
          </div>
          <div className="h-full w-full">
          {showWarningBadge && (
            <div className="flex items-center justify-center p-2">
              <Badge
                color="warning"
                icon={
                  ExclamationCircleIcon as React.FC<
                    React.SVGProps<SVGSVGElement>
                  >
                }
                size={"sm"}
              >
                Assigning order for{" "}
                {selectedShipmentsMemo &&
                  formatDateWithDay(
                    selectedShipmentsMemo.firstEffectiveStopDate
                  )}
              </Badge>
            </div>
          )}
          {hasNoSearchResults && (
            <div className="flex flex-col items-center justify-center py-10 px-4">
              <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 dark:text-gray-600" />
              <span className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                No drivers match &ldquo;{driverSearchTerm}&rdquo;
              </span>
            </div>
          )}
          <Loading
            loading={getLoadingState(dispatchPreviewLoading)}
            text={"Calculating the best routes..."}
            className="min-h-[200px]"
          >
            {!isSearching &&
              availableCandidates &&
              availableCandidates.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <Car className="w-12 h-12 text-gray-300 dark:text-gray-600" />
                  <span className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    No recommended drivers
                  </span>
                </div>
              )}
            {availableCandidates &&
              availableCandidates.length > 0 &&
              availableCandidates.map((item, i) => (
                <div key={i}>
                  <ExpandableDriverCard
                    showBorder={true}
                    title={`${item.driver.firstName} ${item.driver.lastName}`}
                    fullName={item.fullName || ""}
                    subText={item.subText}
                    badgeText={item.badgeText}
                    badgeColor={item.badgeColor}
                    image={item.image}
                    onClick={() => {
                      setSelectedWidgetItem(item);
                      setIsAssignDriverModalOpen(true);
                      setSelectedDriver(item.driver);
                      findDriverRankingAndSet(item.driver);
                    }}
                    expanded={false}
                    onExpand={() => {}}
                    onCollapse={() => {}}
                    isKeyboardSelected={keyBoardSelectedDriverId === item.id}
                    driver={item.driver}
                    showAssignedLabels={isShowDriverLabelsEnabled}
                    showDriverPay={isShowDriverPayEnabled}
                    showDailyEarnings={isShowDailyEarningsEnabled}
                    dailyEarningsSummary={item.dailyEarnings}
                  />
                </div>
              ))}
          </Loading>

          <Loading
            loading={getLoadingState(isOtherOnlineLoading)}
            text={"Loading other online drivers..."}
          >
            {filteredExcludedCandidates.length > 0 && (
              <div
                className="p-2 pl-4 pt-0 text-xs font-light mx-auto text-gray-700 dark:text-gray-300"
                style={{
                  maxWidth:
                    MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN,
                }}
              >
                Other Online Drivers ({filteredExcludedCandidates.length})
              </div>
            )}
            {filteredExcludedCandidates.map((item, i) => (
                <div key={i}>
                  <ExpandableDriverCard
                    showBorder={true}
                    title={`${item.driver.firstName} ${item.driver.lastName}`}
                    fullName={item.fullName || ""}
                    subText={item.subText}
                    badgeText={item.badgeText}
                    badgeColor={item.badgeColor}
                    image={item.image}
                    onClick={() => {
                      setSelectedWidgetItem(item);
                      setIsAssignDriverModalOpen(true);
                      setSelectedDriver(item.driver);
                      findDriverRankingAndSet(item.driver);
                    }}
                    expanded={false}
                    onExpand={() => {}}
                    onCollapse={() => {}}
                    isKeyboardSelected={keyBoardSelectedDriverId === item.id}
                    driver={item.driver}
                    showAssignedLabels={isShowDriverLabelsEnabled}
                    showDriverPay={isShowDriverPayEnabled}
                    showDailyEarnings={isShowDailyEarningsEnabled}
                    dailyEarningsSummary={item.dailyEarnings}
                  />
                </div>
              ))}
          </Loading>

          <Loading
            loading={getLoadingState(FetchDriverTasksByDateAndStatusLoading)}
            text={"Loading offline drivers..."}
          >
            {filteredUnavailableCandidates.length > 0 && (
              <div
                className="p-2 pl-4 pt-0 text-xs font-light mx-auto text-gray-700 dark:text-gray-300"
                style={{
                  maxWidth:
                    MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN,
                }}
              >
                Offline drivers ({filteredUnavailableCandidates.length})
              </div>
            )}

            {filteredUnavailableCandidates.map((item, i) => (
                <div key={i} className="opacity-50">
                  <ExpandableDriverCard
                    title={`${item.driver.firstName} ${item.driver.lastName}`}
                    fullName={item.fullName || ""}
                    subText={item.subText}
                    badgeText={item.badgeText}
                    badgeColor={item.badgeColor}
                    image={item.image}
                    onClick={() => {
                      setSelectedWidgetItem(item);
                      setIsAssignDriverModalOpen(true);
                      setSelectedDriver(item.driver);
                      findDriverRankingAndSet(item.driver);
                    }}
                    expanded={false}
                    onExpand={() => {}}
                    onCollapse={() => {}}
                    driver={item.driver}
                    showAssignedLabels={isShowDriverLabelsEnabled}
                    showDriverPay={isShowDriverPayEnabled}
                    showDailyEarnings={isShowDailyEarningsEnabled}
                    dailyEarningsSummary={item.dailyEarnings}
                  />
                </div>
              ))}
          </Loading>

          <Loading
            loading={getLoadingState(FetchDriverTasksByDateAndStatusLoading)}
          >
            {!isOtherOnlineLoading &&
              filteredSpecializedCandidates.length > 0 && (
                <div
                  className="p-2 pl-4 pt-0 text-xs font-light mx-auto text-gray-700 dark:text-gray-300"
                  style={{
                    maxWidth:
                      MAX_WIDTH_TO_HANDLE_IN_BETWEEN_NEGATIVE_SPACE_IN_LARGE_SCREEN,
                  }}
                >
                  Specialized drivers ({filteredSpecializedCandidates.length})
                </div>
              )}

            {!isOtherOnlineLoading &&
              filteredSpecializedCandidates.length > 0 && (
                <div>
                  {filteredSpecializedCandidates.map((item, i) => (
                    <div
                      key={i}
                      className={classNames({
                        "opacity-50": !item.isActive,
                      })}
                    >
                      <ExpandableDriverCard
                        title={`${item.driver.firstName} ${item.driver.lastName}`}
                        fullName={item.fullName || ""}
                        subText={item.subText}
                        badgeText={item.badgeText}
                        badgeColor={item.badgeColor}
                        image={item.image}
                        onClick={() => {
                          setSelectedWidgetItem(item);
                          setIsAssignDriverModalOpen(true);
                          setSelectedDriver(item.driver);
                          findDriverRankingAndSet(item.driver);
                        }}
                        expanded={false}
                        onExpand={() => {}}
                        onCollapse={() => {}}
                        driver={item.driver}
                        showAssignedLabels={isShowDriverLabelsEnabled}
                        showDriverPay={isShowDriverPayEnabled}
                        showDailyEarnings={isShowDailyEarningsEnabled}
                        dailyEarningsSummary={item.dailyEarnings}
                      />
                    </div>
                  ))}
                </div>
              )}
          </Loading>
          </div>
        </div>
      </div>
    </>
  );
}

const DispatchCandidatesWidgetWrapper: React.FC<DispatchCandidatesWidgetProps> =
  (props) => {
    return (
      <CandidatesProvider>
        <CandidatesGraphQLProvider>
          <DispatchCandidatesWidget {...props} />
        </CandidatesGraphQLProvider>
      </CandidatesProvider>
    );
  };

export default DispatchCandidatesWidgetWrapper;
